import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Web Speech API dictation for the chat composers. On-device/browser speech
 * recognition (Chrome, Edge, Safari); no backend, no keys. Hindi and
 * Malayalam dictation work through the same API with hi-IN / ml-IN locales.
 */

export type VoiceLang = 'en-IN' | 'hi-IN' | 'ml-IN';

export const VOICE_LANGS: { code: VoiceLang; chip: string; label: string }[] = [
  { code: 'en-IN', chip: 'EN', label: 'English' },
  { code: 'hi-IN', chip: 'हिं', label: 'Hindi' },
  { code: 'ml-IN', chip: 'മല', label: 'Malayalam' },
];

const LANG_KEY = 'voice_lang';

export function getSavedVoiceLang(): VoiceLang {
  const saved = localStorage.getItem(LANG_KEY);
  return VOICE_LANGS.some((l) => l.code === saved) ? (saved as VoiceLang) : 'en-IN';
}

export function saveVoiceLang(lang: VoiceLang) {
  localStorage.setItem(LANG_KEY, lang);
}

type AnySpeechRecognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
};

function getRecognitionCtor(): (new () => AnySpeechRecognition) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition || w.webkitSpeechRecognition || null) as (new () => AnySpeechRecognition) | null;
}

export const speechSupported = typeof window !== 'undefined' && !!getRecognitionCtor();

interface UseSpeechInputOptions {
  /** Final transcript chunks — append them to the composer input. */
  onFinal: (text: string) => void;
}

export function useSpeechInput({ onFinal }: UseSpeechInputOptions) {
  const [isListening, setIsListening] = useState(false);
  const [lang, setLangState] = useState<VoiceLang>(getSavedVoiceLang);
  const recRef = useRef<AnySpeechRecognition | null>(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const stop = useCallback(() => {
    recRef.current?.stop();
    setIsListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    // A fresh instance per session avoids stale-state bugs across restarts.
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal && r[0]?.transcript) onFinalRef.current(r[0].transcript.trim());
      }
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = () => setIsListening(false);
    recRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
    } catch {
      setIsListening(false);
    }
  }, [lang]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  const setLang = useCallback((l: VoiceLang) => {
    saveVoiceLang(l);
    setLangState(l);
    // Switching language mid-dictation restarts recognition in the new locale.
    if (recRef.current && isListening) recRef.current.stop();
  }, [isListening]);

  useEffect(() => () => recRef.current?.abort(), []);

  return { isSupported: speechSupported, isListening, toggle, lang, setLang };
}
